import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { MARKETING_BASE, marketingUrl } from "@/lib/marketingUrl";

const LOGO_PRIMARY_DARK = "/brand/logo/df-primary-on-dark.png";

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const { reloadOrg } = useBranding();
    const nav = useNavigate();
    const [sp] = useSearchParams();
    const next = sp.get("next") || "/app/dashboard";

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            await reloadOrg();
            toast.success("Welcome back");
            nav(next);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthShell testid="page-login" title="Sign in" bgImage="/brand/photography/welcome.jpg">
            <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                <div>
                    <Label htmlFor="email" className="text-arch-white/80 text-xs uppercase tracking-[0.15em]">Email</Label>
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="mt-2 bg-obsidian/60 border-arch-white/20 text-arch-white h-11" required />
                </div>
                <div>
                    <div className="flex items-center justify-between">
                        <Label htmlFor="password" className="text-arch-white/80 text-xs uppercase tracking-[0.15em]">Password</Label>
                        <Link to="/forgot-password" className="text-[11px] text-arch-white/50 hover:text-arch-white">Forgot?</Link>
                    </div>
                    <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="mt-2 bg-obsidian/60 border-arch-white/20 text-arch-white h-11" required />
                </div>
                <button type="submit" disabled={loading} className="w-full bg-navy border border-ice/50 text-arch-white px-6 py-3.5 text-sm hover:bg-navy/80 hover:border-ice disabled:opacity-50 uppercase tracking-[0.2em] transition-all" data-testid="login-submit">
                    {loading ? "Signing in…" : "Sign in"}
                </button>
                <div className="text-center pt-3">
                    <Link to="/register" className="text-xs text-ice hover:text-arch-white inline-flex items-center gap-1.5 uppercase tracking-[0.15em]" data-testid="login-cta-register">
                        New here? Start your free trial <ArrowRight size={12} />
                    </Link>
                </div>
            </form>
        </AuthShell>
    );
}

/**
 * Two-column editorial shell.
 *   LEFT:  full-bleed cinematic photo + BIG DF primary lockup + italic tagline
 *   RIGHT: clean form panel
 *   Mobile: stacked (photo hero at top, form below)
 */
export function AuthShell({ title, children, testid, bgImage = "/brand/photography/development.jpg" }) {
    return (
        <div className="min-h-screen bg-obsidian text-arch-white" data-testid={testid}>
            {/* Minimal top nav — back link, present across breakpoints */}
            <nav className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-6 py-5 md:px-10">
                <a
                    href={MARKETING_BASE}
                    className="text-[10px] md:text-[11px] uppercase tracking-[0.2em] text-arch-white/70 hover:text-arch-white inline-flex items-center gap-2"
                    data-testid="auth-back-to-marketing"
                >
                    <ArrowLeft size={12} /> Back to thedreamforgecompany.com
                </a>
                <a
                    href={MARKETING_BASE}
                    className="hidden md:inline text-[10px] uppercase tracking-[0.25em] text-arch-white/40 hover:text-arch-white"
                    data-testid="auth-nav-marketing"
                >
                    thedreamforgecompany.com ↗
                </a>
            </nav>

            <div className="grid min-h-screen lg:grid-cols-2">
                {/* LEFT — cinematic hero */}
                <div className="relative overflow-hidden bg-obsidian min-h-[45vh] lg:min-h-screen" data-testid="auth-hero">
                    <motion.img
                        src={bgImage}
                        alt=""
                        aria-hidden="true"
                        initial={{ scale: 1.08 }}
                        animate={{ scale: 1.0 }}
                        transition={{ duration: 20, ease: "easeOut" }}
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ objectPosition: "center 68%" }}
                    />
                    {/* Stronger Midnight Navy → Obsidian overlay to mute baked-in text */}
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                "radial-gradient(120% 100% at 50% 55%, rgba(15,39,71,0.55) 0%, rgba(17,19,21,0.88) 62%, rgba(17,19,21,0.96) 100%)",
                        }}
                        aria-hidden="true"
                    />
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                "linear-gradient(180deg, rgba(17,19,21,0.85) 0%, rgba(17,19,21,0.35) 22%, rgba(17,19,21,0.35) 78%, rgba(17,19,21,0.85) 100%)",
                        }}
                        aria-hidden="true"
                    />

                    {/* Big logo + tagline */}
                    <div className="relative z-10 h-full min-h-[45vh] lg:min-h-screen flex flex-col items-center justify-center px-8 py-16 text-center">
                        <motion.img
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, ease: "easeOut", delay: 0.2 }}
                            src={LOGO_PRIMARY_DARK}
                            alt="The Dream Forge Company"
                            className="w-[240px] md:w-[300px] lg:w-[340px] xl:w-[380px] h-auto max-w-[80%]"
                            data-testid="auth-hero-logo"
                        />
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, ease: "easeOut", delay: 0.5 }}
                            className="mt-8 lg:mt-10"
                            data-testid="auth-hero-tagline"
                        >
                            <div className="font-display italic text-2xl md:text-3xl lg:text-4xl leading-tight text-ice">
                                Where Vision Is
                            </div>
                            <div className="font-display italic text-2xl md:text-3xl lg:text-4xl leading-tight text-ice">
                                Forged Into Reality
                            </div>
                            <div className="mt-6 text-[10px] uppercase tracking-[0.35em] text-arch-white/50">
                                Development · Construction · Capital · Technology
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* RIGHT — form panel */}
                <div className="relative flex items-center justify-center bg-obsidian px-6 py-14 md:py-24 lg:px-16">
                    <div className="w-full max-w-md">
                        <div className="mb-8" data-testid="auth-form-heading">
                            <h1 className="font-display italic text-3xl md:text-4xl tracking-tight leading-none">{title}</h1>
                        </div>
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}
